/**
 * Host portal → Team: who may use this portal, and what they may do.
 *
 * Two lists sit behind this screen and the operator needs to understand both
 * (workers/connect-api/src/handlers/team.ts):
 *   • Cloudflare Access — the gate. Being on it is what lets you sign in, with
 *     a one-time code Cloudflare emails you. No passwords exist.
 *   • admin_users — the role each of those people has here.
 * Inviting writes both. When they disagree (someone added to Access by hand in
 * the Cloudflare dashboard, or a row whose Access entry has gone), this screen
 * says so rather than quietly papering over it.
 */
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { KeyRound, RefreshCw, ShieldCheck, UserPlus, Users } from "lucide-react";
import {
  Badge,
  Banner,
  Button,
  Card,
  CardContent,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@alight/ui";
import { usePortalSeo } from "@alight/enrolment-ui";
import { formatDay } from "../../components/admin-bits";
import {
  ApiError,
  ROLE_LABELS,
  fetchMe,
  fetchTeam,
  inviteMember,
  removeMember,
  setMemberRole,
  type Role,
  type TeamMember,
  type TeamResult,
} from "../../lib/team";

export const Route = createFileRoute("/admin/team")({
  component: AdminTeam,
});

const ROLES: Role[] = ["admin", "super_admin"];

/** Deliberately loose — the Worker is the authority. This catches typos. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Super admins first, then alphabetical — the people who can act, on top. */
function byRoleThenEmail(a: TeamMember, b: TeamMember): number {
  if (a.role !== b.role) return a.role === "super_admin" ? -1 : 1;
  return a.email.localeCompare(b.email);
}

function AdminTeam() {
  usePortalSeo("Team");

  const [data, setData] = useState<TeamResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<Role>("admin");
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);

  const [busyEmail, setBusyEmail] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Stamps our own last_seen_at before the list is read back, so "Last
      // signed in" means something the moment anyone opens this screen. Its
      // failure is not worth failing the page over.
      await fetchMe().catch(() => null);
      setData(await fetchTeam());
    } catch (err) {
      setData(null);
      setError(err instanceof ApiError ? err.message : "Couldn't load the team.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const canManage = data?.can_manage ?? false;
  const accessReady = Boolean(data?.access_configured) && !data?.access_error;
  const members = [...(data?.members ?? [])].sort(byRoleThenEmail);
  const accessEmails = data?.access_emails ?? [];
  const recorded = new Set(members.map((m) => m.email));
  // Can sign in, but nobody has given them a role here — treated as Admin.
  const accessOnly = accessEmails.filter((e) => !recorded.has(e));
  // Recorded here, but not on the Access policy — they cannot get in at all.
  const missingFromAccess = accessReady ? members.filter((m) => !accessEmails.includes(m.email)) : [];

  async function submitInvite(e: FormEvent) {
    e.preventDefault();
    const email = inviteEmail.trim().toLowerCase();
    setInviteError(null);
    setNotice(null);
    setActionError(null);
    if (!EMAIL_RE.test(email)) {
      setInviteError("That doesn't look like an email address.");
      return;
    }
    setInviting(true);
    try {
      const res = await inviteMember(email, inviteRole);
      setInviteEmail("");
      setInviteRole("admin");
      setNotice(
        res.emailed
          ? `${res.member.email} can sign in now — we've emailed them the link.`
          : `${res.member.email} can sign in now, but the invite email didn't send. Send them the portal link yourself.`,
      );
      await load();
    } catch (err) {
      setInviteError(err instanceof ApiError ? err.message : "Couldn't add that person.");
    } finally {
      setInviting(false);
    }
  }

  async function changeRole(member: TeamMember, role: Role) {
    if (role === member.role) return;
    setBusyEmail(member.email);
    setNotice(null);
    setActionError(null);
    try {
      await setMemberRole(member.email, role);
      setNotice(`${member.email} is now ${ROLE_LABELS[role].toLowerCase()}.`);
      await load();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "Couldn't change that role.");
    } finally {
      setBusyEmail(null);
    }
  }

  async function remove(email: string) {
    setBusyEmail(email);
    setNotice(null);
    setActionError(null);
    try {
      await removeMember(email);
      setConfirming(null);
      setNotice(`${email} can no longer sign in.`);
      await load();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "Couldn't remove that person.");
    } finally {
      setBusyEmail(null);
    }
  }

  return (
    <section className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-bold text-ink">Team</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted">
            Everyone who can use this portal. There are no passwords — someone on this list enters
            their email at the sign-in screen and Cloudflare emails them a 6-digit one-time code.
            Adding someone lets them in straight away; removing them revokes their access
            immediately.
          </p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          <RefreshCw className={loading ? "h-4 w-4 animate-spin" : "h-4 w-4"} aria-hidden />
          Refresh
        </Button>
      </header>

      {error && (
        <Banner variant="error" title="Couldn't load the team">
          {error}{" "}
          <button
            type="button"
            onClick={() => void load()}
            className="font-semibold text-primary underline-offset-2 hover:underline"
          >
            Retry
          </button>
        </Banner>
      )}

      {notice && <Banner variant="success">{notice}</Banner>}
      {actionError && <Banner variant="error">{actionError}</Banner>}

      {data && !data.access_configured && (
        <Banner variant="warning" title="Adding and removing people is switched off">
          {data.access_error ??
            "This Worker has no Cloudflare API token, so it can't change who is allowed to sign in."}
        </Banner>
      )}

      {data?.access_configured && data.access_error && (
        <Banner variant="warning" title="Couldn't read the Cloudflare sign-in list">
          {data.access_error} The roles below are still correct.
        </Banner>
      )}

      {!canManage && data && (
        <Banner variant="info">
          You can see who is on the team. Only a super admin can invite people, change roles or
          remove someone.
        </Banner>
      )}

      {canManage && (
        <Card>
          <CardContent className="p-5">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-ink">
              <UserPlus className="h-5 w-5 text-primary" aria-hidden />
              Invite someone
            </h2>
            <p className="mt-1 text-sm text-muted">
              We add their address to the sign-in list and email them the portal link. They can sign
              in as soon as they get it.
            </p>
            {/* noValidate: our own message is friendlier than the browser's
                native tooltip, and it is wired to the field for screen readers
                via aria-describedby. The Worker validates again regardless. */}
            <form
              noValidate
              className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end"
              onSubmit={(e) => void submitInvite(e)}
            >
              <div className="min-w-0 flex-1 space-y-1.5">
                <Label htmlFor="invite-email">Email address</Label>
                <Input
                  id="invite-email"
                  type="email"
                  autoComplete="off"
                  placeholder="name@example.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  aria-invalid={inviteError ? true : undefined}
                  aria-describedby={inviteError ? "invite-error" : undefined}
                  disabled={inviting || !data?.access_configured}
                />
              </div>
              <div className="space-y-1.5 sm:w-48">
                <Label htmlFor="invite-role">Role</Label>
                <Select
                  value={inviteRole}
                  onValueChange={(v) => setInviteRole(v as Role)}
                  disabled={inviting || !data?.access_configured}
                >
                  <SelectTrigger id="invite-role" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => (
                      <SelectItem key={r} value={r}>
                        {ROLE_LABELS[r]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" disabled={inviting || !data?.access_configured}>
                {inviting ? "Adding…" : "Send invite"}
              </Button>
            </form>
            {inviteError && (
              <p id="invite-error" role="alert" className="mt-2 text-sm font-medium text-error">
                {inviteError}
              </p>
            )}
            <p className="mt-3 text-xs text-muted">
              <strong className="font-semibold text-ink">Admin</strong> can use the whole portal.{" "}
              <strong className="font-semibold text-ink">Super admin</strong> can also manage this
              screen.
            </p>
          </CardContent>
        </Card>
      )}

      {missingFromAccess.length > 0 && (
        <Banner variant="warning" title="These people can't actually sign in">
          {missingFromAccess.map((m) => m.email).join(", ")} {missingFromAccess.length === 1 ? "has" : "have"}{" "}
          a role here but {missingFromAccess.length === 1 ? "is" : "are"} not on the Cloudflare
          sign-in list. Remove and re-invite {missingFromAccess.length === 1 ? "them" : "each of them"} to put it right.
        </Banner>
      )}

      {loading && !data ? (
        <div className="rounded-card border border-border bg-card px-4 py-10 text-center text-sm text-muted">
          Loading the team…
        </div>
      ) : members.length === 0 && !error ? (
        <div className="rounded-card border border-border bg-card px-4 py-12 text-center">
          <Users className="mx-auto h-8 w-8 text-muted/60" aria-hidden />
          <p className="mt-3 text-sm text-muted">
            Nobody is recorded yet. Invite someone above and they'll appear here.
          </p>
        </div>
      ) : (
        members.length > 0 && (
          <Card>
            <CardContent className="overflow-x-auto p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Person</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Invited by</TableHead>
                    <TableHead>Added</TableHead>
                    <TableHead>Last signed in</TableHead>
                    {canManage && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members.map((m) => {
                    const isMe = m.email === data?.me;
                    const busy = busyEmail === m.email;
                    return (
                      <TableRow key={m.email}>
                        <TableCell className="max-w-[14rem] truncate font-medium text-ink" title={m.email}>
                          {m.email}
                          {isMe && (
                            <Badge variant="outline" className="ml-2">
                              You
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {canManage ? (
                            <Select
                              value={m.role}
                              onValueChange={(v) => void changeRole(m, v as Role)}
                              disabled={busy}
                            >
                              <SelectTrigger
                                className="w-[9.5rem]"
                                aria-label={`Role for ${m.email}`}
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {ROLES.map((r) => (
                                  <SelectItem key={r} value={r}>
                                    {ROLE_LABELS[r]}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : m.role === "super_admin" ? (
                            <Badge variant="accent">
                              <ShieldCheck className="mr-1 h-3.5 w-3.5" aria-hidden />
                              Super admin
                            </Badge>
                          ) : (
                            <Badge variant="muted">Admin</Badge>
                          )}
                        </TableCell>
                        <TableCell className="max-w-[12rem] truncate text-muted" title={m.invited_by ?? ""}>
                          {m.invited_by ?? "—"}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-muted">{formatDay(m.invited_at)}</TableCell>
                        <TableCell className="whitespace-nowrap text-muted">
                          {m.last_seen_at ? formatDay(m.last_seen_at) : "Not yet"}
                        </TableCell>
                        {canManage && (
                          <TableCell className="text-right">
                            {confirming === m.email ? (
                              <span className="flex flex-wrap justify-end gap-2">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="accent"
                                  onClick={() => void remove(m.email)}
                                  disabled={busy}
                                >
                                  {busy ? "Removing…" : "Really remove"}
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setConfirming(null)}
                                  disabled={busy}
                                >
                                  Cancel
                                </Button>
                              </span>
                            ) : (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setActionError(null);
                                  setConfirming(m.email);
                                }}
                                disabled={busy || isMe}
                                title={isMe ? "You can't remove yourself" : undefined}
                              >
                                Remove
                              </Button>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )
      )}

      {accessOnly.length > 0 && (
        <Card>
          <CardContent className="p-5">
            <h2 className="flex items-center gap-2 font-heading text-base font-bold text-ink">
              <KeyRound className="h-4 w-4 text-warning" aria-hidden />
              Can sign in, but has no role recorded
            </h2>
            <p className="mt-1 text-sm text-muted">
              {accessOnly.length === 1 ? "This address is" : "These addresses are"} on the Cloudflare
              sign-in list but {accessOnly.length === 1 ? "was" : "were"} never added here — probably
              added straight in the Cloudflare dashboard. {accessOnly.length === 1 ? "They are" : "They are"}{" "}
              treated as Admin until someone records a role.
            </p>
            <ul className="mt-3 divide-y divide-border rounded-card border border-border text-sm">
              {accessOnly.map((email) => (
                <li key={email} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                  <span className="min-w-0 break-all text-ink">{email}</span>
                  {canManage && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => void remove(email)}
                      disabled={busyEmail === email}
                    >
                      {busyEmail === email ? "Removing…" : "Remove access"}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </section>
  );
}
